import { useState, Suspense, lazy, ElementType } from 'react';
import { Status } from '@uds/button';
import { Product } from '@uds/product';
import { BehaviorType } from 'core/ribbons/cta';
import { Behavior } from 'domain/constants/callToAction';
import { useExternalRoutes } from 'domain/selectors/common';

import {
  useHandleAddToBasketClick,
  useHandleConfigureClick,
  handlePlayVideoClick,
  useHandleAnalytics,
  useHandleSetVisibilityClick,
} from './callToActionHandlers';

const VideoModal = lazy(() => import('presentation/components/modal/Video'));

interface Props {
  id?: string;
  ribbonId?: string;
  product?: Product;
  behavior?: BehaviorType;
  [key: string]: any;
}

export default function (Component: ElementType) {
  return function CallToActionWrapper({ id, ribbonId, behavior, product, ...props }: Props) {
    const [addingStatus, setAddingStatus] = useState(Status.DEFAULT);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const externalRoutes = useExternalRoutes();
    const handleAnalytics = useHandleAnalytics({ ribbonId });
    const handleAddToBasketClick = useHandleAddToBasketClick({
      product,
      setAddingStatus,
      handleAnalytics: () => handleAnalytics({ behavior }),
      postAction: behavior != null && 'postAction' in behavior ? behavior.postAction : undefined,
    });
    const handleConfigureClick = useHandleConfigureClick();
    const handleSetVisibilityClick = useHandleSetVisibilityClick();
    // add an analytics prefixed `id` to the cta if required
    const ctaId = behavior?.analytics?.linkAttribution == null ? id : `usf-cta-${behavior.analytics.linkAttribution}`;
    const actionType = behavior?.type;
    const toggleVideoModal = () => {
      return setIsVideoModalOpen((isOpen) => !isOpen);
    };

    const handleClick = (() => {
      if (actionType === Behavior.ADD_TO_BASKET) {
        return () => handleAddToBasketClick({ behavior });
      }
      if (actionType === Behavior.CONFIGURE) {
        return () => handleConfigureClick({ behavior });
      }
      if (actionType === Behavior.PLAY_VIDEO) {
        return () => handlePlayVideoClick({ toggleVideoModal });
      }
      if (actionType === Behavior.SET_VISIBILITY) {
        return () => handleSetVisibilityClick({ behavior });
      }
      if (actionType === Behavior.NAVIGATION) {
        return () => handleAnalytics({ behavior });
      }

      return () => null;
    })();

    if (actionType === Behavior.PLAY_VIDEO) {
      return (
        <>
          <Suspense fallback={null}>
            {isVideoModalOpen && (
              <VideoModal
                isOpen={isVideoModalOpen}
                videoId={behavior?.videoId}
                provider={behavior?.provider}
                closeModal={toggleVideoModal}
              />
            )}
          </Suspense>
          <Component
            id={ctaId}
            ribbonId={ribbonId}
            behavior={behavior}
            product={product}
            handleClick={handleClick}
            {...props}
          />
        </>
      );
    }

    return (
      <Component
        id={ctaId}
        ribbonId={ribbonId}
        behavior={behavior}
        product={product}
        handleClick={handleClick}
        addingStatus={addingStatus}
        externalRoutes={externalRoutes}
        {...props}
      />
    );
  };
}
